import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Filter } from 'lucide-react';
import { useRouteEntity } from '../lib/queries/entityById';
import { pathForRoute } from '../lib/router';
import { entityLabel } from '../lib/pageTitle';

// EntityLink — the ONE way this console renders a reference to another record.
//
// The class of bug it exists to kill: a reference is rendered as flat text in one place
// (the events list' Organizer / Venue cells), as an indistinguishable gray crumb in
// another (the event hero, where the organizer WAS a button but looked exactly like the
// date and the start time next to it), and as a full chevron row in a third — so an
// operator can't tell which references they can open, and mostly assumes none of them.
//
// The rule this encodes: a reference is clickable if and only if the target is a REAL
// record we can open — a nav handler plus an id the router can resolve. Otherwise it is
// plain text carrying the reason. That distinction is not cosmetic, it's the data:
//   • 117/117 EventPages on prod carry `organizationId` → the organizer is always openable.
//   • 66/117 carry a resolvable `venueId`; mookh has 0/195. The rest hold a TYPED ADDRESS
//     ("PCEA Kahawa Sukari Church") — a real place, but not a Venue record. Offering a
//     link there would 404; saying so on hover is honest.
//
// Never render a link affordance for something that can't be opened (see the
// reason-on-hover ecosystem: every inert control explains itself).

// A REFERENCE NAMES ITSELF FROM THE ONE STORE. Given the detail `route` it opens and its `id`, the
// name comes from whatever already loaded that record — any list seeds it — and re-renders when one
// does. No fetch: unnamed, it is still a link to the record's page. `value` (a name the row itself
// carries) wins; `fallback` answers only when neither is known.
export const useEntityName = ({ value, route, id, fallback }) => {
  const stored = useRouteEntity(value ? null : route, id);
  return value || entityLabel(stored) || fallback || null;
};
export const EntityName = (props) => <>{useEntityName(props) ?? '—'}</>;

export const EntityLink = ({
  value: given,
  id,
  onNav: onNavGiven,
  route,                  // the detail route this reference opens — names it from the store, and opens it
  fallback,
  entity,                 // what the id names ('organizer', 'venue') — used in the reason
  icon: Icon,
  reason,                 // override the default "not linked" explanation
  className = '',
  tone = 'text-[#4B5563]',  // colour when it is NOT a link (a link is always house blue)
  iconClassName = 'w-3.5 h-3.5 text-[#6B7280]',
  truncate = false,
  // THE SECOND VERB. A reference opens its record; inside a list that can be scoped by it, it can
  // also narrow the list to it. Given only by a page that honours that scope (the event lens on
  // Orders, Transactions, Payment plans), so the button never offers a filter nothing applies.
  onScope = null,
}) => {
  const navigate = useNavigate();
  const value = useEntityName({ value: given, route, id, fallback });
  const onNav = onNavGiven || (route ? ({ id: target }) => navigate({ to: pathForRoute(route, { id: target }) }) : null);
  const text = value == null || value === '' ? '—' : value;
  const linkable = Boolean(onNav && id);
  const body = (
    <>
      {Icon && <Icon className={`${iconClassName} flex-shrink-0`} />}
      <span className={truncate ? 'truncate' : undefined}>{text}</span>
    </>
  );

  // With a scope button the pair is one unit, so the caller's sizing goes on the pair.
  const scoped = Boolean(onScope && id);
  const own = scoped ? '' : className;
  const reference = !linkable ? (
      <span
        className={`inline-flex items-center gap-1.5 min-w-0 ${tone} ${own}`}
        title={reason || (entity
          ? `No ${entity} record is linked here — this is a name stored on the row, not a record that can be opened.`
          : String(text))}
      >
        {body}
      </span>
  ) : (
    <button
      type="button"
      // Table rows navigate on click (Table.jsx puts onRowClick on the <tr>), so a
      // reference cell must stop the bubble or it silently opens the wrong record.
      onClick={(e) => { e.stopPropagation(); onNav({ id, name: typeof value === 'string' ? value : undefined }); }}
      title={`Open ${text}`}
      // NOT a hyperlink blue. This console's palette is warm neutral ink (#1F2937) with
      // colour reserved for MEANING — red/amber/green are severity, and #1E40AF appears in
      // ~10 places total. Painting every cross-entity reference blue would have made
      // "openable" the loudest thing on a page whose actual signal is money and status.
      // The mark instead: the reference keeps the ink of the text around it and carries a
      // hairline rule in the border tone (#D1CFC7), which resolves to full ink on hover.
      // Present enough to read as a reference at a glance, quiet enough to stay content.
      className={`inline-flex items-center gap-1.5 min-w-0 text-left ${tone} underline decoration-[#D1CFC7] decoration-1 underline-offset-[3px] hover:decoration-[#1F2937] hover:text-[#1F2937] ${own}`}
    >
      {body}
    </button>
  );

  if (!scoped) return reference;
  const scopeLabel = `Show only ${text}`;
  return (
    // On hover a filter sits at the right end of the name's own space — over the ellipsis of a long
    // name — on a faint solid fill. It takes no room of its own, so nothing moves. Hover-only on
    // purpose: on a phone, a funnel on every row would be noise; the top bar's picker filters there.
    <span className={`group/ref relative flex items-center w-full min-w-0 ${className}`}>
      {reference}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onScope({ id, name: typeof value === 'string' ? value : undefined }); }}
        title={scopeLabel}
        aria-label={scopeLabel}
        className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 rounded cursor-pointer flex items-center justify-center bg-[#F2F1ED] text-[#4B5563] opacity-0 group-hover/ref:opacity-100 focus-visible:opacity-100 hover:bg-[#E6E4E1] hover:text-[#1F2937] focus-visible:outline-none"
      >
        <Filter className="w-3 h-3" fill="currentColor" strokeWidth={2} />
      </button>
    </span>
  );
};
